import { useTranslation } from 'react-i18next';
import {
  usePinnedFiltersStore,
  type SlotDraft,
} from '@/store/pinned-filters-store';

type Props = {
  column: string;
};

/**
 * Pinned date slot: inline From/To date inputs. Writes a `between` draft when
 * both inputs are filled; clears when either is empty.
 */
export function DateSlot({ column }: Props): JSX.Element {
  const { t } = useTranslation();
  const draft = usePinnedFiltersStore((s) => s.drafts[column]);
  const setDraft = usePinnedFiltersStore((s) => s.setDraft);

  const [from, to] = readRange(draft);

  function emit(nextFrom: string, nextTo: string): void {
    if (!nextFrom && !nextTo) {
      setDraft(column, null);
      return;
    }
    let next: SlotDraft;
    if (nextFrom && nextTo) {
      next = { operator: 'between', value: [nextFrom, nextTo] };
    } else if (nextFrom) {
      next = { operator: 'gte', value: nextFrom };
    } else {
      next = { operator: 'lte', value: nextTo };
    }
    setDraft(column, next);
  }

  return (
    <div className="flex gap-1 items-stretch">
      <div className="flex flex-col flex-1 min-w-0 gap-0.5">
        <label
          className="text-[10px]"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('filters.pinned.dateFrom')}
        </label>
        <input
          type="date"
          value={from}
          onChange={(e) => emit(e.target.value, to)}
          aria-label={t('filters.pinned.dateFromAria', { column })}
          className="w-full text-xs p-1 rounded"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-fg)',
          }}
        />
      </div>
      <div className="flex flex-col flex-1 min-w-0 gap-0.5">
        <label
          className="text-[10px]"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('filters.pinned.dateTo')}
        </label>
        <input
          type="date"
          value={to}
          onChange={(e) => emit(from, e.target.value)}
          aria-label={t('filters.pinned.dateToAria', { column })}
          className="w-full text-xs p-1 rounded"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-fg)',
          }}
        />
      </div>
      <button
        type="button"
        onClick={() => setDraft(column, null)}
        disabled={!from && !to}
        aria-label={t('filters.pinned.dateResetAria', { column })}
        className="text-xs px-1.5 rounded disabled:opacity-40 self-end mb-0.5"
        style={{
          border: '1px solid var(--color-border)',
          color: 'var(--color-muted)',
        }}
      >
        ×
      </button>
    </div>
  );
}

function readRange(draft: SlotDraft | null | undefined): [string, string] {
  if (!draft) return ['', ''];
  if (draft.operator === 'between' && Array.isArray(draft.value)) {
    const [a, b] = draft.value as Array<string | number>;
    return [a == null ? '' : String(a), b == null ? '' : String(b)];
  }
  if (draft.operator === 'gte') {
    return [draft.value == null ? '' : String(draft.value), ''];
  }
  if (draft.operator === 'lte') {
    return ['', draft.value == null ? '' : String(draft.value)];
  }
  return ['', ''];
}
